import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import logo from '../assets/images/logo.svg';
import {
  fetchKitAssignment,
  submitKitAssignments,
} from '../services/kitAssignment';

const NL_MONTHS = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december',
];

function getDaysInMonth(month, year) {
  if (!month || !year) return 31;
  return new Date(year, month, 0).getDate();
}

function ageFromParts(day, month, year) {
  const d = parseInt(day, 10);
  const m = parseInt(month, 10);
  const y = parseInt(year, 10);
  if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(y)) return null;
  if (m < 1 || m > 12 || d < 1 || d > getDaysInMonth(m, y) || y < 1900) return null;
  const dob = new Date(y, m - 1, d);
  if (dob > new Date()) return null;
  const today = new Date();
  let age = today.getFullYear() - y;
  const monthDiff = today.getMonth() - (m - 1);
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d)) age -= 1;
  return age;
}

function formatDobLong(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  const monthName = NL_MONTHS[parseInt(m, 10) - 1];
  if (!monthName) return iso;
  return `${parseInt(d, 10)} ${monthName} ${y}`;
}

function splitName(fullName) {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  };
}

function emptyDraft() {
  return {
    recipientType: '',
    firstName: '',
    lastName: '',
    email: '',
    day: '',
    month: '',
    year: '',
  };
}

function draftFromAssignment(assignment, buyer) {
  if (!assignment) return emptyDraft();
  const names = splitName(
    assignment.is_buyer
      ? buyer?.full_name
      : `${assignment.first_name || ''} ${assignment.last_name || ''}`.trim()
  );
  const iso = assignment.date_of_birth || '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return {
    recipientType: assignment.recipient_type || '',
    firstName: names.firstName,
    lastName: names.lastName,
    email: assignment.email || (assignment.is_buyer ? buyer?.email || '' : ''),
    day: d ? String(parseInt(d, 10)).padStart(2, '0') : '',
    month: m ? String(parseInt(m, 10)).padStart(2, '0') : '',
    year: y || '',
  };
}

export default function KitToewijzenPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [session, setSession] = React.useState(null);
  const [loadError, setLoadError] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(true);
  const [activeIndex, setActiveIndex] = React.useState(null);
  const [editingIndex, setEditingIndex] = React.useState(null);
  const [drafts, setDrafts] = React.useState({});
  const [submittingIndex, setSubmittingIndex] = React.useState(null);
  const pollRef = React.useRef(0);

  const token = (searchParams.get('token') || '').trim();
  const source = (searchParams.get('source') || '').trim();

  const loadSession = React.useCallback(async (nextToken) => {
    const data = await fetchKitAssignment(nextToken);
    setSession(data);
    setLoadError('');
    const nextDrafts = {};
    (data?.kits || []).forEach((kit) => {
      nextDrafts[kit.index] = draftFromAssignment(kit.assignment, data.buyer);
    });
    setDrafts(nextDrafts);
    const firstOpen = (data?.kits || []).find((kit) => !kit.assigned);
    // Only expand unassigned kits; assigned ones stay collapsed until "wijzigen".
    setActiveIndex(firstOpen ? firstOpen.index : null);
    setEditingIndex(null);
    return data;
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    async function start() {
      setIsLoading(true);
      try {
        if (token) {
          await loadSession(token);
          return;
        }
        setLoadError('Deze link is ongeldig. Open de pagina opnieuw via je bestelling.');
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            err?.response?.data?.errors?.token?.[0] ||
              err?.response?.data?.message ||
              'We konden deze bestelling niet laden. Probeer het later opnieuw.'
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    start();
    return () => {
      cancelled = true;
    };
  }, [token, loadSession]);

  React.useEffect(() => {
    if (!token || !session || session.kits_ready || session.is_used) return undefined;
    if (pollRef.current >= 15) return undefined;
    const id = setTimeout(async () => {
      pollRef.current += 1;
      try {
        await loadSession(token);
      } catch (_) {
        // keep polling
      }
    }, 2000);
    return () => clearTimeout(id);
  }, [token, session, loadSession]);

  const kits = session?.kits || [];
  const kitCount = kits.length;
  const assignedCount = kits.filter((kit) => kit.assigned).length;
  const allAssigned = kitCount > 0 && assignedCount === kitCount;
  const activeKit =
    kits.find((kit) => kit.index === activeIndex) ||
    kits.find((kit) => !kit.assigned) ||
    kits[0];
  const draft = drafts[activeKit?.index] || emptyDraft();

  const openKitForm = (kit) => {
    const isAlreadyOpen =
      activeIndex === kit.index &&
      (!kit.assigned || editingIndex === kit.index);

    if (isAlreadyOpen) {
      const nextOpen = kits.find(
        (item) => !item.assigned && item.index !== kit.index
      );
      setActiveIndex(nextOpen ? nextOpen.index : null);
      setEditingIndex(null);
      return;
    }

    setActiveIndex(kit.index);
    setEditingIndex(kit.assigned ? kit.index : null);
  };

  const updateDraft = (index, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [index]: { ...(prev[index] || emptyDraft()), ...patch },
    }));
  };

  const chooseRecipient = (index, recipientType) => {
    const buyer = session?.buyer || {};
    const names = splitName(buyer.full_name);
    if (recipientType === 'self') {
      updateDraft(index, {
        recipientType: 'self',
        firstName: names.firstName,
        lastName: names.lastName,
        email: buyer.email || '',
      });
    } else {
      updateDraft(index, {
        recipientType: 'other',
        firstName: '',
        lastName: '',
        email: '',
      });
    }
    setActiveIndex(index);
  };

  const handleAssign = async (kit, finalizeAll = false) => {
    const current = drafts[kit.index] || emptyDraft();
    if (!current.recipientType) {
      toast.error('Kies of deze kit voor jou is of voor iemand anders.');
      return;
    }
    const age = ageFromParts(current.day, current.month, current.year);
    if (age == null) {
      toast.error('Vul een geldige geboortedatum in.');
      return;
    }
    if (current.recipientType === 'other') {
      if (!current.firstName.trim() || !current.lastName.trim() || !current.email.trim()) {
        toast.error('Vul naam en e-mailadres van de deelnemer in.');
        return;
      }
    }
    const dateOfBirth = `${current.year}-${String(current.month).padStart(2, '0')}-${String(current.day).padStart(2, '0')}`;
    const payload = [{
      kit_index: kit.index,
      recipient_type: current.recipientType,
      date_of_birth: dateOfBirth,
      first_name: current.firstName.trim(),
      last_name: current.lastName.trim(),
      email: current.email.trim(),
    }];

    if (finalizeAll) {
      const extra = kits
        .filter((item) => item.index !== kit.index)
        .map((item) => {
          const d = drafts[item.index] || emptyDraft();
          if (!item.assigned && (!d.recipientType || !d.year)) return null;
          const source = item.assigned ? item.assignment : d;
          if (item.assigned && source) {
            return {
              kit_index: item.index,
              recipient_type: source.recipient_type,
              date_of_birth: source.date_of_birth,
              first_name: source.first_name || '',
              last_name: source.last_name || '',
              email: source.email || '',
            };
          }
          return {
            kit_index: item.index,
            recipient_type: d.recipientType,
            date_of_birth: `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`,
            first_name: d.firstName.trim(),
            last_name: d.lastName.trim(),
            email: d.email.trim(),
          };
        })
        .filter(Boolean);
      payload.push(...extra);
    }

    setSubmittingIndex(kit.index);
    try {
      const data = await submitKitAssignments(token, payload, finalizeAll || kitCount === 1, source);
      setSession(data);
      const nextDrafts = {};
      (data?.kits || []).forEach((item) => {
        nextDrafts[item.index] = draftFromAssignment(item.assignment, data.buyer);
      });
      setDrafts(nextDrafts);
      const nextOpen = (data?.kits || []).find((item) => !item.assigned);
      setActiveIndex(nextOpen ? nextOpen.index : null);
      setEditingIndex(null);
      if (data?.is_used || ((data?.kits || []).every((item) => item.assigned))) {
        toast.success('Kits zijn gekoppeld. Bedankt!');
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.errors?.token?.[0] ||
          err?.response?.data?.errors?.detail ||
          err?.response?.data?.message ||
          'Opslaan mislukt. Probeer het opnieuw.'
      );
    } finally {
      setSubmittingIndex(null);
    }
  };

  const heading = kitCount === 1 ? 'Aan wie koppelen we deze kit?' : 'Aan wie koppelen we deze kits?';
  const age = ageFromParts(draft.day, draft.month, draft.year);
  const finishDisabled = !allAssigned || Boolean(submittingIndex);
  const confirmDisabled =
    !draft.recipientType ||
    age == null ||
    Boolean(submittingIndex) ||
    (draft.recipientType === 'other' && (!draft.firstName.trim() || !draft.lastName.trim() || !draft.email.trim()));

  return (
    <div className="min-h-screen bg-[#F3F1EE] text-[#1E2426]">
      <header className="bg-white border-b border-[#E8E2DE]">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src={logo} alt="biowell" className="h-9 w-auto" />
          <p className="text-[11px] tracking-[0.16em] font-poppins text-[#6F7A82]">
            BEVEILIGDE OMGEVING
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        {isLoading ? (
          <p className="text-center text-[#6F7A82] font-poppins">Bestelling laden...</p>
        ) : loadError ? (
          <div className="bg-white rounded-2xl border border-[#E8E2DE] p-8 text-center">
            <h1 className="font-libreBaskerville text-2xl mb-3">Link ongeldig</h1>
            <p className="font-poppins text-[#6F7A82]">{loadError}</p>
            <button
              type="button"
              onClick={() => navigate('/signin')}
              className="mt-6 h-12 px-6 rounded-xl bg-black text-white font-poppins"
            >
              Naar inloggen
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 text-sm font-poppins text-[#1F7A5A] mb-6">
              <span className="w-6 h-6 rounded-full bg-[#5db393] text-white grid place-items-center">✓</span>
              <span>
                Betaling ontvangen. Bestelling {session.order_number || ''}
                {kitCount ? ` · ${kitCount} kit${kitCount === 1 ? '' : 's'}` : ''}
              </span>
            </div>

            <h1 className="font-libreBaskerville text-3xl md:text-4xl mb-3">{heading}</h1>
            <p className="font-poppins text-[#6F7A82] max-w-2xl mb-8">
              De uitslag wordt beoordeeld op naam en leeftijd van degene die prikt. Vul dat nu in, dan staat de kit meteen klaar.
            </p>

            {kitCount > 1 && (
              <div className="mb-6">
                <p className="text-xs tracking-[0.14em] font-poppins text-[#6F7A82] mb-2">
                  {assignedCount} VAN {kitCount} KITS TOEGEWEZEN
                </p>
                <div className="h-1.5 rounded-full bg-[#E5E7EB]">
                  <div
                    className="h-1.5 rounded-full bg-[#5db393]"
                    style={{ width: `${kitCount ? (assignedCount / kitCount) * 100 : 0}%` }}
                  />
                </div>
              </div>
            )}

            <div className="space-y-4">
              {kits.map((kit) => {
                const isExpanded =
                  kit.index === activeIndex &&
                  !session.is_used &&
                  (!kit.assigned || editingIndex === kit.index);
                const kitDraft = drafts[kit.index] || emptyDraft();
                const kitAge = ageFromParts(kitDraft.day, kitDraft.month, kitDraft.year);
                return (
                  <section key={kit.index} className="bg-white rounded-2xl border border-[#E8E2DE] p-5 md:p-6">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="inline-block text-[11px] tracking-[0.14em] font-poppins text-[#6F7A82] bg-[#F3F1EE] px-2 py-1 rounded mb-2">
                          {kit.label}
                        </p>
                        <h2 className="font-poppins font-semibold text-lg">{kit.product_name}</h2>
                        {kit.assigned ? (
                          <p className="mt-2 text-sm text-[#1F7A5A] font-poppins">
                            ✓ {kit.assignment?.first_name} {kit.assignment?.last_name}
                            {kit.assignment?.is_buyer ? ' (jij)' : ''}
                            {kit.assignment?.date_of_birth ? ` · ${formatDobLong(kit.assignment.date_of_birth)}` : ''}
                          </p>
                        ) : (
                          <p className="mt-1 text-sm text-[#6F7A82]">Nog geen deelnemer</p>
                        )}
                      </div>
                      {!session.is_used && (
                        <button
                          type="button"
                          className="text-sm underline font-poppins text-[#1E2426]"
                          onClick={() => openKitForm(kit)}
                        >
                          {kit.assigned
                            ? isExpanded
                              ? 'inklappen'
                              : 'wijzigen'
                            : isExpanded
                              ? 'inklappen'
                              : 'toewijzen'}
                        </button>
                      )}
                    </div>

                    {isExpanded && (
                      <div className="mt-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => chooseRecipient(kit.index, 'self')}
                            className={`h-12 rounded-xl border font-poppins ${
                              kitDraft.recipientType === 'self'
                                ? 'bg-black text-white border-black'
                                : 'bg-white text-[#1E2426] border-[#D1D5DB]'
                            }`}
                          >
                            Aan mijzelf
                          </button>
                          <button
                            type="button"
                            onClick={() => chooseRecipient(kit.index, 'other')}
                            className={`h-12 rounded-xl border font-poppins ${
                              kitDraft.recipientType === 'other'
                                ? 'bg-black text-white border-black'
                                : 'bg-white text-[#1E2426] border-[#D1D5DB]'
                            }`}
                          >
                            Aan iemand anders
                          </button>
                        </div>

                        {kitDraft.recipientType === 'self' && (
                          <div className="mt-5 rounded-xl bg-[#F8F6F4] p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-xs tracking-[0.12em] text-[#6F7A82] mb-1">DEELNEMER</p>
                                <p className="font-poppins font-semibold">{session.buyer?.full_name || '—'}</p>
                                <p className="font-poppins text-sm text-[#6F7A82]">{session.buyer?.email || '—'}</p>
                              </div>
                              <span className="text-[10px] tracking-[0.12em] text-[#6F7A82]">UIT JE BESTELLING</span>
                            </div>
                            <p className="mt-2 text-xs text-[#6F7A82]">
                              Naam of e-mailadres wijzigen kan na het inloggen in je dashboard.
                            </p>
                          </div>
                        )}

                        {kitDraft.recipientType === 'other' && (
                          <div className="mt-5 space-y-3">
                            <label className="block">
                              <span className="text-xs tracking-[0.12em] text-[#6F7A82]">NAAM DEELNEMER</span>
                              <div className="mt-1 grid grid-cols-2 gap-3">
                                <input
                                  className="h-12 rounded-xl border border-[#D1D5DB] px-4 font-poppins"
                                  placeholder="Voornaam"
                                  value={kitDraft.firstName}
                                  onChange={(e) => updateDraft(kit.index, { firstName: e.target.value })}
                                />
                                <input
                                  className="h-12 rounded-xl border border-[#D1D5DB] px-4 font-poppins"
                                  placeholder="Achternaam"
                                  value={kitDraft.lastName}
                                  onChange={(e) => updateDraft(kit.index, { lastName: e.target.value })}
                                />
                              </div>
                            </label>
                            <label className="block">
                              <span className="text-xs tracking-[0.12em] text-[#6F7A82]">E-MAILADRES DEELNEMER</span>
                              <input
                                className="mt-1 w-full h-12 rounded-xl border border-[#D1D5DB] px-4 font-poppins"
                                placeholder="naam@voorbeeld.nl"
                                value={kitDraft.email}
                                onChange={(e) => updateDraft(kit.index, { email: e.target.value })}
                              />
                            </label>
                          </div>
                        )}

                        {kitDraft.recipientType && (
                          <div className="mt-5">
                            <p className="text-xs tracking-[0.12em] text-[#6F7A82] mb-2">GEBOORTEDATUM</p>
                            <div className="grid grid-cols-3 gap-3">
                              <input
                                className="h-12 rounded-xl border border-[#D1D5DB] px-3 text-center font-poppins"
                                placeholder="DD"
                                maxLength={2}
                                value={kitDraft.day}
                                onChange={(e) => updateDraft(kit.index, { day: e.target.value.replace(/\D/g, '') })}
                              />
                              <input
                                className="h-12 rounded-xl border border-[#D1D5DB] px-3 text-center font-poppins"
                                placeholder="MM"
                                maxLength={2}
                                value={kitDraft.month}
                                onChange={(e) => updateDraft(kit.index, { month: e.target.value.replace(/\D/g, '') })}
                              />
                              <input
                                className="h-12 rounded-xl border border-[#D1D5DB] px-3 text-center font-poppins"
                                placeholder="JJJJ"
                                maxLength={4}
                                value={kitDraft.year}
                                onChange={(e) => updateDraft(kit.index, { year: e.target.value.replace(/\D/g, '') })}
                              />
                            </div>
                            {kitAge != null && (
                              <p className="mt-2 text-sm font-poppins">
                                <span className="font-semibold">{kitAge} jaar.</span>{' '}
                                Het lab gebruikt de referentiewaarden voor deze leeftijd.
                              </p>
                            )}
                          </div>
                        )}

                        {kitCount > 1 && (
                          <button
                            type="button"
                            disabled={confirmDisabled}
                            onClick={() => handleAssign(kit)}
                            className="mt-5 w-full h-12 rounded-xl bg-black text-white font-poppins disabled:bg-[#D1D5DB] disabled:text-[#6F7A82]"
                          >
                            {submittingIndex === kit.index ? 'Bezig…' : 'Kit toewijzen'}
                          </button>
                        )}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>

            <button
              type="button"
              disabled={!session.is_used && (kitCount === 1 ? confirmDisabled : finishDisabled)}
              onClick={() => {
                if (session.is_used) {
                  navigate('/signin');
                  return;
                }
                handleAssign(activeKit, kitCount > 1);
              }}
              className="mt-6 w-full h-12 rounded-xl bg-black text-white font-poppins disabled:bg-[#D1D5DB] disabled:text-[#6F7A82]"
            >
              {session.is_used
                ? 'Naar inloggen'
                : submittingIndex != null
                  ? 'Bezig…'
                  : kitCount === 1
                    ? 'Bevestigen en afronden'
                    : 'Toewijzen en afronden'}
            </button>

            {session.is_used && (
              <p className="mt-4 text-center text-[#1F7A5A] font-poppins">
                Alles is opgeslagen. Je kunt dit venster sluiten.
              </p>
            )}

            <p className="mt-4 text-xs leading-5 text-[#6F7A82] font-poppins">
              Elke deelnemer ontvangt een e-mail om gegevens en toestemming te bevestigen. Gegevens worden alleen gedeeld met het lab en de Biowell-arts, versleuteld volgens de AVG.
            </p>

            <section className="mt-10">
              <h3 className="text-xs tracking-[0.16em] text-[#6F7A82] mb-4">WAT ER HIERNA GEBEURT</h3>
              <ol className="space-y-4 font-poppins text-sm">
                <li><span className="font-semibold">01</span> Kit op de mat, inclusief retourenvelop. 2 werkdagen.</li>
                <li><span className="font-semibold">02</span> Afname thuis met de Tasso, terugsturen. 5 minuten.</li>
                <li><span className="font-semibold">03</span> Uitslag en toelichting in je dashboard. 3 werkdagen.</li>
              </ol>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
